import React, { useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

import { GIButton } from 'app-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { configs } from '@constants/configs/configs';
import { DEFAULT_FILE_ACCEPTED_TYPES, DEFAULT_FILE_MAX_SIZE, formatBytes } from '@utils/miscellaneous/file.utils';
import {
    StyledDragLabel,
    StyledDropzoneContainer,
    StyledFileDescription,
    StyledFileIcon,
    StyledFileInfo,
    StyledFileName,
    StyledFileProperties,
    StyledFileRow,
    StyledFileSize,
    StyledFileThumbnail,
    StyledFileThumbnailContainer,
    StyledImageThumbnail,
    StyledOrLabel,
    StyledProgressBar,
    StyledRemoveButton,
} from './fileUploadDragDrop.styled';
import { ConditionalRendering } from '@components/common/conditional-rendering';

export interface FileUploadProps {
    onFilesAccepted: (files: File[]) => void;
    onFileError?: (errors: string[][]) => void;
    multiple?: boolean;
    maxSize?: number;
    accept?: { [key: string]: string[] };
    descriptionLabelId?: string;
    dragLabelId?: string;
    uploadLabelId?: string;
    acceptFileLabelId?: string;
    maxFileSizeLabelId?: string;
    isRequired?: boolean;
}

const FileThumbnail = ({ file }: { file: File }) => {
    const isImage = file.type.startsWith('image/');
    const previewUrl = URL.createObjectURL(file);

    return (
        <StyledFileThumbnailContainer>
            {isImage ? (
                <StyledImageThumbnail src={previewUrl} alt={file.name} onLoad={() => URL.revokeObjectURL(previewUrl)} />
            ) : (
                <StyledFileThumbnail>📄</StyledFileThumbnail>
            )}
        </StyledFileThumbnailContainer>
    );
};

function FileUploadDragDrop({
    onFilesAccepted,
    onFileError,
    multiple = false,
    maxSize = DEFAULT_FILE_MAX_SIZE,
    accept = DEFAULT_FILE_ACCEPTED_TYPES,
    descriptionLabelId = 'common.file.upload.description',
    dragLabelId = 'common.file.upload.drag.label',
    uploadLabelId = 'common.file.upload.upload.label',
    acceptFileLabelId = 'common.file.upload.accept.file.label',
    maxFileSizeLabelId = 'common.file.upload.max.file.size.label',
    isRequired = false,
}: FileUploadProps) {
    const intl = useIntl();
    const [isUploading, setIsUploading] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const { getRootProps, getInputProps } = useDropzone({
        accept,
        maxSize,
        multiple,
        onDrop: (acceptedFiles, fileRejections) => handleUpload(acceptedFiles, fileRejections),
    });

    const handleRemove = (index: number) => {
        const newFiles = [...files];
        newFiles.splice(index, 1);
        onFilesAccepted(newFiles);
        setFiles(newFiles);
    };

    const handleUpload = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        if (fileRejections.length > 0) {
            const errorMessages = fileRejections.map(rejection => {
                return rejection.errors.map(err => err.code);
            });
            onFileError?.(errorMessages);
            return;
        }

        setIsUploading(true);
        onFilesAccepted(acceptedFiles);
        setFiles([...files, ...acceptedFiles]);
        setIsUploading(false);
    };

    return (
        <div>
            <StyledFileDescription className={`required-${isRequired}`}>
                <FormattedMessage id={descriptionLabelId} />
            </StyledFileDescription>
            <ConditionalRendering isTrue={multiple || files.length === 0}>
                <StyledDropzoneContainer {...getRootProps()}>
                    <input aria-labelledby={dragLabelId} {...getInputProps()} />
                    <img alt="file upload" src={configs.assets.images.fileUploadIcon} />
                    <StyledDragLabel id={dragLabelId}>
                        <FormattedMessage id={dragLabelId} />
                    </StyledDragLabel>
                    <StyledOrLabel>{intl.formatMessage({ id: 'common.or' }).toUpperCase()}</StyledOrLabel>
                    <GIButton type="button" id="upload-files" variant={'secondary'} labelId={uploadLabelId} />
                </StyledDropzoneContainer>
                <StyledFileProperties>
                    <span>
                        <FormattedMessage
                            id={acceptFileLabelId}
                            values={{ strong: (chunks: any) => <strong>{chunks}</strong> }}
                        />
                    </span>
                    <span>
                        <FormattedMessage
                            id={maxFileSizeLabelId}
                            values={{ strong: (chunks: any) => <strong>{chunks}</strong> }}
                        />
                    </span>
                </StyledFileProperties>
            </ConditionalRendering>
            <ConditionalRendering isTrue={files && files.length > 0}>
                <FormattedMessage id="common.file.uploaded.file.label" values={{ count: files.length }} tagName="h5" />
                {files.map((file, index) => (
                    <>
                        <StyledFileRow key={index}>
                            <StyledFileIcon>
                                <span role="img" aria-label="file">
                                    <FileThumbnail file={file} />
                                </span>
                            </StyledFileIcon>
                            <StyledFileInfo>
                                <StyledFileName>{file.name}</StyledFileName>
                                <StyledFileSize>{formatBytes(file.size)}</StyledFileSize>
                            </StyledFileInfo>
                            <StyledRemoveButton
                                type="button"
                                onClick={() => handleRemove(index)}
                                aria-label="Remove file"
                            >
                                <FontAwesomeIcon icon={'trash'} />
                            </StyledRemoveButton>
                        </StyledFileRow>
                        <ConditionalRendering isTrue={isUploading}>
                            <StyledProgressBar percent={100} />
                        </ConditionalRendering>
                    </>
                ))}
            </ConditionalRendering>
        </div>
    );
}

export default FileUploadDragDrop;
